import React, { useState } from 'react';

type Props = {
  open: boolean;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: (payload: { internalNote?: string; customerMessage?: string; includeCustomerMessage: boolean }) => void | Promise<void>;
};

export default function RejectUserModal({ open, submitting = false, onCancel, onConfirm }: Props) {
  const [internalNote, setInternalNote] = useState('');
  const [customerMessage, setCustomerMessage] = useState('');
  const [includeCustomerMessage, setIncludeCustomerMessage] = useState(false);
  const [working, setWorking] = useState(false);

  if (!open) return null;
  const busy = submitting || working;

  async function handleConfirm() {
    setWorking(true);
    try {
      await onConfirm({
        internalNote: internalNote.trim() || undefined,
        customerMessage: customerMessage.trim() || undefined,
        includeCustomerMessage,
      });
      setInternalNote('');
      setCustomerMessage('');
      setIncludeCustomerMessage(false);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onCancel} role="presentation">
      <div
        className="modal card card-elevated confirm-action-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2>Reject user?</h2>
        <p className="modal-lead">
          Internal notes are visible only to Scoutly platform administrators. The applicant will only receive a generic
          rejection email unless you explicitly include a customer-facing message.
        </p>

        <label className="confirm-prompt-modal__field">
          Internal note (staff only)
          <textarea
            value={internalNote}
            placeholder="Optional"
            disabled={busy}
            onChange={(e) => setInternalNote(e.target.value)}
            rows={3}
          />
        </label>

        <label className="confirm-prompt-modal__field">
          Message to applicant (optional)
          <textarea
            value={customerMessage}
            placeholder="Optional"
            disabled={busy || !includeCustomerMessage}
            onChange={(e) => setCustomerMessage(e.target.value)}
            rows={3}
          />
        </label>

        <label style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
          <input
            type="checkbox"
            checked={includeCustomerMessage}
            disabled={busy}
            onChange={(e) => setIncludeCustomerMessage(e.target.checked)}
          />
          Include the applicant message in the rejection email
        </label>

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void handleConfirm()}>
            {busy ? 'Working…' : 'Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}
